import PropTypes from "prop-types";
import React, { useMemo } from "react";
// @mui
import {
  Box,
  Stack,
  Button,
  Drawer,
  Divider,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
// components
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import MemberSidebarTableOptions from "./MemberSidebarTableOptions";
import { useGetSitesQuery } from "../../../features/sites/sitesApiSlice";
import { gradArray } from "../../../components/form/fields/inputFields";

// ----------------------------------------------------------------------

MembersFilterSidebar.propTypes = {
  openFilter: PropTypes.bool,
  onOpenFilter: PropTypes.func,
  onCloseFilter: PropTypes.func,

  // table column options (unchanged logic)
  tableColumns: PropTypes.array,
  handleChangeTableColumns: PropTypes.func,

  // filters
  filters: PropTypes.shape({
    sites: PropTypes.arrayOf(PropTypes.string),
    degrees: PropTypes.arrayOf(PropTypes.number),
    statuses: PropTypes.arrayOf(PropTypes.string),
  }),
  onChangeFilters: PropTypes.func,
};

const ITEM_HEIGHT = 36;
const MenuProps = {
  PaperProps: {
    style: { maxHeight: ITEM_HEIGHT * 8, width: 260 },
  },
};

const STATUS_OPTIONS = [
  { value: "active", label: "Aktiv" },
  { value: "frozen", label: "Eingefroren" },
  { value: "terminated", label: "Gekündigt" },
  { value: "pending", label: "Wartend" },
  { value: "rejected", label: "Abgelehnt" },
];

export default function MembersFilterSidebar({
  openFilter,
  onOpenFilter,
  onCloseFilter,
  tableColumns,
  handleChangeTableColumns,
  filters,
  onChangeFilters,
}) {
  const { data: sites = [] } = useGetSitesQuery();

  const siteOptions = useMemo(
    () =>
      sites.map((s) => ({
        id: String(s._id || s.id),
        label: s.siteName,
      })),
    [sites]
  );

  const degreeOptions = useMemo(
    () =>
      gradArray
        .map((g, idx) =>
          g?.label ? { id: idx, value: idx, label: g.label } : null
        )
        .filter(Boolean),
    []
  );

  const handleSitesChange = (e) => {
    onChangeFilters?.({ ...filters, sites: [...e.target.value] });
  };
  const handleDegreesChange = (e) => {
    onChangeFilters?.({ ...filters, degrees: [...e.target.value] });
  };
  const handleStatusesChange = (e) => {
    onChangeFilters?.({ ...filters, statuses: [...e.target.value] });
  };

  const handleClearFilters = () => {
    // Default should be only ACTIVE visible
    onChangeFilters?.({ sites: [], degrees: [], statuses: ["active"] });
  };

  return (
    <>
      <Button
        sx={{ mr: 2 }}
        disableRipple
        color="secondary"
        variant="contained"
        endIcon={<Iconify icon="mdi:tune" />}
        onClick={onOpenFilter}
      >
        Filter & Optionen
      </Button>

      <Drawer
        anchor="right"
        open={openFilter}
        onClose={onCloseFilter}
        PaperProps={{
          sx: { width: 320, border: "none", overflow: "hidden" },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1, py: 2 }}
        >
          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            Filter & Optionen
          </Typography>
          <IconButton onClick={onCloseFilter}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>

        <Divider />

        <Scrollbar>
          <Stack spacing={2} sx={{ p: 2 }}>
            {/* ---- Filters: Standort, Grad, Status ---- */}
            <Typography variant="subtitle1">Filter</Typography>

            <FormControl fullWidth>
              <InputLabel id="site-filter-label">Standort</InputLabel>
              <Select
                multiple
                labelId="site-filter-label"
                value={filters?.sites || []}
                onChange={handleSitesChange}
                input={<OutlinedInput label="Standort" />}
                renderValue={(selected) =>
                  siteOptions
                    .filter((s) => selected.includes(s.id))
                    .map((s) => s.label)
                    .join(", ")
                }
                MenuProps={MenuProps}
              >
                {siteOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox checked={(filters?.sites || []).includes(s.id)} />
                    <ListItemText primary={s.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="degree-filter-label">Grad</InputLabel>
              <Select
                multiple
                labelId="degree-filter-label"
                value={filters?.degrees || []}
                onChange={handleDegreesChange}
                input={<OutlinedInput label="Grad" />}
                renderValue={(selected) =>
                  degreeOptions
                    .filter((d) => selected.includes(d.value))
                    .map((d) => d.label)
                    .join(", ")
                }
                MenuProps={MenuProps}
              >
                {degreeOptions.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    <Checkbox
                      checked={(filters?.degrees || []).includes(d.value)}
                    />
                    <ListItemText primary={d.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                multiple
                labelId="status-filter-label"
                value={filters?.statuses || ["active"]}
                onChange={handleStatusesChange}
                input={<OutlinedInput label="Status" />}
                renderValue={(selected) =>
                  STATUS_OPTIONS.filter((o) => selected.includes(o.value))
                    .map((o) => o.label)
                    .join(", ")
                }
                MenuProps={MenuProps}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Checkbox
                      checked={(filters?.statuses || ["active"]).includes(
                        o.value
                      )}
                    />
                    <ListItemText primary={o.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Button
                fullWidth
                size="large"
                color="inherit"
                variant="outlined"
                startIcon={<Iconify icon="ic:round-clear-all" />}
                onClick={handleClearFilters}
              >
                Filter zurücksetzen
              </Button>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* ---- Column options (kept) ---- */}
            <Typography variant="subtitle1">Spalten</Typography>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<Iconify icon="material-symbols:expand-more" />}
              >
                <Typography variant="body2">Tabellenspalten</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <MemberSidebarTableOptions
                  tableColumns={tableColumns}
                  handleChangeTableColumns={handleChangeTableColumns}
                />
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Scrollbar>

        <Box sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="secondary"
            endIcon={<Iconify icon="mdi:check" />}
            onClick={onCloseFilter}
          >
            Anwenden
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
